import { useState } from "react";
import {
  DEFAULT_NOTE_TYPES,
  NOTE_DIRECTIONS,
  useCreateNoteMutation,
  useDeleteNotesByIdsMutation,
  useUpdateNoteMutation,
} from "@/services/api";
import { useModal } from "@/features/modals/lib";
import { getCurrentDate } from "@/shared/utils";
import { useAlert } from "@/shared/hooks";
import { getErrorMessage } from "@/services/errors";
import type { Note } from "@/services/api";
import type { NoteManagerCurrentNote } from "../types";
import type { NotesTableRecord } from "@/common/NotesTable/types";
import type { NoteEditorFormValues } from "@/features/NoteEditor/model";

export const useNoteManagerService = (noteType: Note["noteType"]["name"]) => {
  const {
    showModal,
    hideModal: hideDeleteNotesModal,
    isModalOpen: isDeleteNotesModalOpen,
  } = useModal();
  const [currentNote, setCurrentNote] = useState<NoteManagerCurrentNote | null>(null);
  const [tabs, setTabs] = useState<NoteManagerCurrentNote[]>([]);
  const [selectedNotes, setSelectedNotes] = useState<Note[]>([]);
  const [updateNote] = useUpdateNoteMutation();
  const [createNote] = useCreateNoteMutation();
  const [deleteNotesByIds] = useDeleteNotesByIdsMutation();

  const { callError } = useAlert();

  const addNewTab = (record: NotesTableRecord, index: number) => {
    setCurrentNote({
      index: index,
      note: record,
    });
    if (!tabs?.find((item) => item.note.id === record.id)) {
      setTabs((prev) => [...prev, { index: index, note: record }]);
    }
  };

  const upsertNote = (data: NoteEditorFormValues["currentNote"]) => {
    if (data.dueDate?.length && data.noteType.name !== DEFAULT_NOTE_TYPES.DEFAULT_COMMITMENT) {
      data.dueDate = "";
    }

    if (data.id?.includes("new-")) {
      createNote({
        attachments: data.attachments.map((item) => ({
          id: item.id ?? "",
          fileDescription: item.fileDescription ?? "",
          fileDto: {
            fileId: item.fileDto.fileId as string,
            fileName: item.fileDto.fileName ?? "",
            createdDate: "",
            type: item.fileDto.type as string,
          },
        })),
        direction: data.direction ?? null,
        dueDate: data.dueDate ?? "",
        important: data.important ?? null,
        noteDate: data.noteDate ?? "",
        noteType: { name: data.noteType.name },
        personId: data.personId as string,
        status: data.status ?? null,
        text: data.text ?? "",
        title: data.title ?? null,
      })
        .unwrap()
        .then((note) => {
          setTabs((prev) => {
            const updatedNoteIndex = prev.findIndex((item) => item.note.id === data.id);
            const newTabs = [...prev];
            if (!newTabs[updatedNoteIndex]?.note) return prev;
            newTabs[updatedNoteIndex].note = note;
            return newTabs;
          });
        })
        .catch((e) => {
          callError({ text: getErrorMessage(e.code) });
        });
      return;
    }
    updateNote({
      attachments: data.attachments.map((item) => ({
        id: item.id ?? null,
        fileDescription: "item.fileDescription",
        fileDto: {
          fileId: item.fileDto.fileId as string,
          fileName: item.fileDto.fileName ?? "",
          createdDate: "",
          type: item.fileDto.type as string,
        },
      })),
      direction: data.direction ?? null,
      dueDate: data.dueDate ?? "",
      id: data.id ?? "",
      important: data.important ?? null,
      noteDate: data.noteDate ?? "",
      noteType: data.noteType,
      personId: data.personId as string,
      status: data.status ?? null,
      text: data.text ?? null,
      title: data.title ?? null,
    })
      .unwrap()
      .then((note) => {
        setTabs((prev) => {
          const updatedNoteIndex = prev.findIndex((item) => item.note.id === note.id);
          const newTabs = [...prev];
          if (!newTabs[updatedNoteIndex]?.note) return prev;
          newTabs[updatedNoteIndex].note = note;
          return newTabs;
        });
        setSelectedNotes((prev) => {
          const updatedNoteIndex = prev.findIndex((item) => item.id === note.id);
          const newTabs = [...prev];
          if (!newTabs[updatedNoteIndex]) return prev;
          newTabs[updatedNoteIndex] = note;
          return newTabs;
        });
      })
      .catch((e) => {
        callError({ text: getErrorMessage(e.code) });
      });
  };

  const removeTab = (id: string, prevId: string, nextId: string) => {
    const nextNote =
      tabs.find((item) => item.note.id === prevId) ?? tabs.find((item) => item.note.id === nextId);
    if (!nextNote) {
      setCurrentNote(null);
      setTabs([]);
      return;
    }
    setCurrentNote(nextNote);
    setTabs((prev) => prev.filter((note) => note.note.id !== id));
  };

  const onTabClick = (id: string) => {
    const note = tabs.find((item) => item.note.id == id);
    if (!note) return;
    setCurrentNote({ index: note.index, note: note.note });
  };

  const createTempNote = () => {
    setTabs((prev) => {
      const lastTabIndex = prev[prev.length - 1]?.index;
      const newNote: NoteManagerCurrentNote = {
        index: Number.isInteger(lastTabIndex) ? lastTabIndex + 1 : 0,
        note: {
          attachments: [],
          direction: NOTE_DIRECTIONS.OUTGOING,
          createdDate: "",
          dueDate: "",
          id: `new-${lastTabIndex}`,
          important: false,
          noteDate: getCurrentDate(),
          noteType: { name: noteType },
          personId: "",
          status: "TO_DO",
          text: "",
          title: "New note",
        },
      };
      setCurrentNote(newNote);
      return [...prev, newNote];
    });
  };

  const openDeleteNotesModal = () => {
    if (!selectedNotes.length) return;
    showModal();
  };

  const deleteSelectedNotes = (deletedNotes: Note[]) => {
    deleteNotesByIds(deletedNotes.map((item) => item.id)).catch((e) => {
      callError({ text: getErrorMessage(e.code) });
    });
    setTabs((prev) => {
      const newTabs = prev.filter(
        (tab) =>
          !deletedNotes.find((row) => {
            const isMatching = row.id === tab.note.id;
            if (isMatching) {
              const isCurrentNote = row.id === currentNote?.note.id;
              if (isCurrentNote) {
                setCurrentNote(null);
              }
            }
            return isMatching;
          }),
      );

      if (newTabs.length > 0) {
        const currentIndex = newTabs.findIndex((tab) => tab.note.id === currentNote?.note.id);

        const nextIndex = currentIndex > 0 ? currentIndex - 1 : 0;

        setCurrentNote(newTabs[nextIndex]);
      }

      return newTabs;
    });

    setSelectedNotes((prev) =>
      prev.filter((item) => {
        if (deletedNotes.find((deleted) => deleted.id === item.id)) {
          return false;
        }
        return true;
      }),
    );
    hideDeleteNotesModal();
  };

  return {
    currentNote,
    upsertNote,
    removeTab,
    createTempNote,
    onTabClick,
    addNewTab,
    isDeleteNotesModalOpen,
    hideDeleteNotesModal,
    deleteSelectedNotes,
    setSelectedNotes,
    selectedNotes,
    openDeleteNotesModal,
    tabs,
  };
};
